// The essay workshop player (profile 'workshop'): the start screen with the
// choice of case studies, the step rail, and the steps. This build plays the
// first two steps (understand the question, revise and recall); the rest show
// a stub in their place and stay locked behind the first unfinished step,
// exactly as they will when built. Every word a learner reads that carries
// the topic comes from the workshop block; the lines here are the engine's
// own, and the ones Leah's rules fix word for word are in WORKSHOP_FIXED_LINES.
//
// State lives in useWorkshop; this file is layout and the step screens.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ActivityResponsePayload } from '../../../../../../types';
import type { ReviseRun, WorkshopBlock, WorkshopEdition, WorkshopRevisionPage } from '../../../../../../lib/revise/types';
import { capitalise, caseStudyList, copyFor, copyVars, labelReuseNote, numberWord, recallKeyFacts, recallLines } from '../../../../../../lib/revise/workshop/steps';
import { compareSummary, evidenceTable, leftOutThemes, overallBoth, rankTally, sortedByRank } from '../../../../../../lib/revise/workshop/argue';
import { cardOf } from '../../../../../../lib/revise/workshop/missed';
import { ESSAY_WORDS_USUAL, PERSPECTIVE_AGREE, PERSPECTIVE_AGREE_TEXT, PERSPECTIVE_USE, checkHeadline, essayPrintHtml, joinList, outlineText, partIdFor, partTitle, thousands, wordCount, type PartId } from '../../../../../../lib/revise/workshop/write';
import { WORKSHOP_WORD_TARGETS } from '../../../../../../lib/revise/types';
import type { ReviseRunSlideData } from '../../../slideTypes';
import { WORKSHOP_FIXED_LINES } from '../../../../../../lib/revise/workshop/copy';
import { fetchReviseProgress, saveReviseDraft } from '../../../../../../services/reviseService';
import { reportError } from '../../../../../../lib/errorHandling';
import { REVISE_STYLES } from '../styles';
import { WORKSHOP_STYLES } from './styles';
import { useWorkshop, type WorkshopApi } from './useWorkshop';

interface Props {
  run: ReviseRun;
  /** The slide's data, needed to write draft rows through the registry. */
  data: ReviseRunSlideData;
  w: WorkshopBlock;
  /** The run's CSS variables, from reviseTokens; passed in so this file never imports the slide. */
  tokens: React.CSSProperties;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  slideId?: string;
  courseId?: string;
  lessonId?: string;
}

/** The pending save per (slide, key), so saves for one draft never overtake each other. */
const draftChains = new Map<string, Promise<void>>();

/** Student entry: loads resume state, then plays. */
export function WorkshopRunStudent({ run, data, w, tokens, onActivityResponse, slideId, courseId, lessonId }: Props) {
  const [progress, setProgress] = useState<Record<string, Record<string, unknown>> | null | undefined>(undefined);
  useEffect(() => {
    let live = true;
    if (!slideId || !courseId) {
      setProgress(null);
      return;
    }
    fetchReviseProgress(courseId, slideId).then((p) => live && setProgress(p));
    return () => {
      live = false;
    };
  }, [slideId, courseId]);

  if (progress === undefined && slideId && courseId) {
    return (
      <div className="rr" style={tokens} data-profile={run.profile} data-board={run.board}>
        <style>{REVISE_STYLES + WORKSHOP_STYLES}</style>
        <div className="rr-stub">Loading…</div>
      </div>
    );
  }
  // Drafts upsert one row per key; without the ids (a preview) they stay in memory.
  // Saves for one key run in order (the timer, a blur and a check can overlap, and
  // an older write landing last would win). A skip for the flag or for staff
  // previewing is expected; anything else is a lost draft and must be seen.
  const onDraft = slideId && courseId && lessonId
    ? (key: string, payload: Record<string, unknown>) => {
        const chainKey = `${slideId}:${key}`;
        const prior = draftChains.get(chainKey) ?? Promise.resolve();
        const next = prior
          .then(() => saveReviseDraft(courseId, lessonId, { id: slideId, data, order: 0 }, key, payload))
          .then((r) => {
            if (!r.written && r.skipReason !== 'flag-off' && r.skipReason !== 'not-enrolled') reportError('reviseWorkshop.saveDraft', new Error(r.skipReason ?? 'not written'), { key });
          })
          .finally(() => {
            if (draftChains.get(chainKey) === next) draftChains.delete(chainKey);
          });
        draftChains.set(chainKey, next);
      }
    : undefined;
  return (
    <div className="rr" style={tokens} data-profile={run.profile} data-board={run.board}>
      <style>{REVISE_STYLES + WORKSHOP_STYLES}</style>
      <WorkshopPlayer w={w} initialProgress={progress ?? null} onActivityResponse={onActivityResponse} onDraft={onDraft} />
    </div>
  );
}

export function WorkshopPlayer({ w, initialProgress, onActivityResponse, onDraft }: { w: WorkshopBlock; initialProgress: Record<string, Record<string, unknown>> | null; onActivityResponse?: Props['onActivityResponse']; onDraft?: (key: string, payload: Record<string, unknown>) => void }) {
  const api = useWorkshop({
    w,
    initialProgress,
    onRecord: (_key, responseData, isCorrect, score) => onActivityResponse?.({ activityType: 'revise-run', mode: 'quiz', responseData, isCorrect, score }),
    onDraft,
  });
  const [coachOpen, setCoachOpen] = useState(false);

  return (
    <div className="rw">
      <div className="rw-band">
        <span className="rr-eyebrow">{w.brand.line}</span>
        <h1>
          {w.brand.title} <span>{w.brand.titleAccent}</span>
        </h1>
        {api.phase === 'steps' && (
          <button type="button" className="rw-essaypill" disabled={api.held} title={api.held ? 'Check your list first' : 'Open your essay'} onClick={() => api.goToStep('essay')}>
            Your essay: {wordCount(api.essayText)} words
          </button>
        )}
        {w.coach && (
          <button type="button" className="rw-coach" onClick={() => setCoachOpen(true)}>
            ? Ask the coach
          </button>
        )}
      </div>
      <div className="rw-body">{api.phase === 'start' ? <StartScreen w={w} api={api} /> : <Steps w={w} api={api} />}</div>
      {api.phase === 'steps' && <Nav api={api} />}
      {coachOpen && w.coach && <CoachQuick w={w} api={api} onClose={() => setCoachOpen(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Start screen: the question, how it works, the choice of case studies.
// ---------------------------------------------------------------------------

function StartScreen({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  const resuming = api.firstOpen > 0 || api.isDone(api.steps[0]);
  const choice = !('fixed' in w.selection) ? w.selection : null;
  const lede = choice ? (choice.lede ?? copyFor(undefined, 'chooseLede', copyVars(w, api.chosen))) : '';
  return (
    <div className="rw-start">
      <div className="rw-startq">
        <span className="rr-eyebrow">The question you will answer</span>
        <p className="rw-q">{w.question}</p>
        <p className="rw-exam">{w.exam}</p>
      </div>
      <p className="rw-lede">{w.intro}</p>
      {choice && (
        <div className="rw-card">
          <span className="rr-eyebrow">{api.complete ? `Your ${api.pick === 2 ? w.unit.plural : w.unit.singular}` : lede}</span>
          <div className="rw-opts" role="group" aria-label="Your choice">
            {Object.entries(w.caseStudies).map(([id, c]) => (
              <button key={id} type="button" className="rw-opt" aria-pressed={api.chosen.includes(id)} onClick={() => api.toggleCase(id)}>
                <b>{c.head}</b>
                {(c.region || c.about) && <span>{[c.region, c.about].filter(Boolean).join('. ')}</span>}
              </button>
            ))}
          </div>
          {api.refused && (
            <div className="rw-note rw-fix" aria-live="polite">
              <p>{api.refused}</p>
            </div>
          )}
          {api.complete && <p className="rw-howto">{capitalise(caseStudyList(w, api.chosen))}.</p>}
          {api.complete && Object.keys(w.caseStudies).length > api.pick && <p className="rw-howto">Switching {w.unit.singular} keeps the work you have done on each one.</p>}
        </div>
      )}
      <div>
        <span className="rr-eyebrow">How it works</span>
        <div className="rw-how" style={{ marginTop: 8 }}>
          {w.how.map((h, i) => (
            <div key={i} className="rw-card">
              <div className="rw-num">{i + 1}</div>
              <h3>{h.head}</h3>
              <p>{h.text}</p>
            </div>
          ))}
        </div>
      </div>
      {w.coach && (
        <div className="rw-coachstrip">
          <b>Your coach</b>
          <span>{w.coachStrip}</span>
        </div>
      )}
      <div className="rw-row">
        <button type="button" className="rr-btn-primary" disabled={!api.complete} onClick={api.begin}>
          {resuming ? 'Carry on where you left off' : 'Start the workshop'}
        </button>
        {api.complete && !api.revDone && !api.revSkipped && (
          <button type="button" className="rr-btn-quiet" onClick={api.skipRevision}>
            Skip the revision
          </button>
        )}
        {!api.complete && <span className="rw-gatehint">Choose your {api.pick === 2 ? `two ${w.unit.plural}` : w.unit.singular} to start.</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Steps: rail, head, body
// ---------------------------------------------------------------------------

function Steps({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  const s = api.step;
  return (
    <div className="rw-wrap">
      <nav className="rw-rail" aria-label="Workshop steps">
        {api.steps.map((st, i) => {
          const skipped = st.id === 'revise' && api.revSkipped && !api.revDone;
          const done = api.isDone(st) && !skipped && i !== api.stepIdx;
          const disabled = api.isLocked(i) || (api.held && i !== api.stepIdx);
          return (
            <button
              key={st.key}
              type="button"
              className={`rw-railbtn${done ? ' rw-done' : ''}`}
              aria-current={i === api.stepIdx ? 'step' : undefined}
              disabled={disabled}
              title={disabled ? (api.held ? 'Check your list first' : 'Finish the earlier steps first') : undefined}
              onClick={() => api.goTo(i, 0)}
            >
              <span className="rw-n">{done ? '✓' : i + 1}</span>
              <span className="rw-lbl">
                {st.title}
                {skipped ? ' (skipped)' : ''}
              </span>
            </button>
          );
        })}
        <button type="button" className="rw-railhome" disabled={api.held} onClick={api.toStart}>
          Back to the start screen
        </button>
      </nav>
      <div className="rw-main" key={`${s?.key}-${api.part}`}>
        <div className="rw-head">
          <div>
            <span className="rr-eyebrow">
              Step {api.stepIdx + 1} of {api.steps.length}
            </span>
            <h2>{s?.title}</h2>
          </div>
          {api.partCount > 1 && (
            <div className="rw-dots" aria-label="Parts of this step">
              {Array.from({ length: api.partCount }, (_, d) => (
                <button key={d} type="button" aria-label={`Part ${d + 1}`} aria-current={d === api.part ? 'true' : undefined} disabled={api.held || (d > api.part && !!api.gate)} onClick={() => api.goTo(api.stepIdx, d)} />
              ))}
              <span className="rw-meta">
                Part {api.part + 1} of {api.partCount}
              </span>
            </div>
          )}
        </div>
        {s?.id === 'question' && <QuestionStep w={w} api={api} />}
        {s?.id === 'revise' && <ReviseStep w={w} api={api} />}
        {s?.id === 'match' && <MatchStep w={w} api={api} />}
        {s?.id === 'argue' && <ArgueStep w={w} api={api} />}
        {s?.id === 'missed' && <MissedStep w={w} api={api} />}
        {s?.id === 'paragraphs' && <ParagraphsStep w={w} api={api} />}
        {s?.id === 'intro' && <IntroStep w={w} api={api} />}
        {s?.id === 'paragraph' && <ParagraphStep w={w} api={api} />}
        {s?.id === 'conclusion' && <ConclusionStep w={w} api={api} />}
        {s?.id === 'essay' && <EssayStep w={w} api={api} />}
      </div>
    </div>
  );
}

function Nav({ api }: { api: WorkshopApi }) {
  const last = api.part + 1 >= api.partCount;
  const nextStep = last ? api.steps[api.stepIdx + 1] : undefined;
  const atEnd = last && !nextStep;
  return (
    <div className="rr-nav">
      <button type="button" onClick={api.prev} disabled={api.held || (api.stepIdx === 0 && api.part === 0)}>
        ← Back
      </button>
      <div className="rr-nav-right" style={{ alignItems: 'center' }}>
        <span className="rw-gatehint" aria-live="polite">
          {api.gate ?? ''}
        </span>
        {!atEnd && (
          <button type="button" className={api.gate ? '' : 'rr-btn-primary'} disabled={!!api.gate} onClick={api.next}>
            {last && nextStep ? `Next: ${nextStep.title} →` : 'Next →'}
          </button>
        )}
      </div>
    </div>
  );
}

function QuestionStrip({ w }: { w: WorkshopBlock }) {
  return (
    <div className="rw-qstrip">
      <span className="rr-eyebrow">The question</span>
      <span>{w.question}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1: understand the question
// ---------------------------------------------------------------------------

function QuestionStep({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  if (api.part === 1) {
    return (
      <>
        <QuestionStrip w={w} />
        <h3 style={{ fontSize: '1.35rem' }}>What each part asks of you</h3>
        <div className="rw-grid">
          {w.asks.map((a) => (
            <div key={a.key + a.head} className="rw-card">
              <span className="rr-eyebrow">{a.name}</span>
              <h4>{a.head}</h4>
              <p>{a.text}</p>
            </div>
          ))}
        </div>
        <div className="rw-card">
          <span className="rr-eyebrow">Watch for</span>
          <h4 style={{ fontSize: '1.2rem' }}>{w.watch.head}</h4>
          <p>{w.watch.text}</p>
        </div>
      </>
    );
  }
  const reuse = labelReuseNote(w);
  const r = api.labelCheck;
  return (
    <>
      <p className="rw-lede">Every paragraph you write has to answer this question. Before you plan anything, work out what it is asking.</p>
      <p className="rw-howto">Pick a label, then select the part of the question it fits.{reuse ? ` ${reuse}` : ''}</p>
      <div className="rw-brushes" role="group" aria-label="Labels">
        {w.labels.map((l) => (
          <button key={l.key} type="button" className={`rw-brush rw-c-${l.key}`} aria-pressed={api.brush === l.key} onClick={() => api.setBrush(l.key)}>
            {l.name}
          </button>
        ))}
      </div>
      <div className="rw-qline" aria-label="The question, in parts">
        {w.parts.map((p, i) => (
          <button key={i} type="button" className={`rw-tok${api.tags[i] ? ` rw-c-${api.tags[i]}` : ''}`} onClick={() => api.tagPart(i)} aria-label={`${p.text}${api.tags[i] ? `, labelled ${w.labels.find((l) => l.key === api.tags[i])?.name ?? api.tags[i]}` : ''}`}>
            {p.text}
          </button>
        ))}
      </div>
      <div className="rw-row">
        <button type="button" className="rr-btn-primary" onClick={api.checkTags}>
          Check my labels
        </button>
      </div>
      {r && (
        <div className={`rw-fb ${r.ok ? 'rw-good' : 'rw-meh'}`} aria-live="polite">
          {r.ok ? `All ${r.total} parts labelled correctly. Go on to see what each part asks of you.` : `${r.correct} of ${r.total} parts labelled correctly. Change the ones listed below and check again.`}
        </div>
      )}
      {r && !r.ok && (
        <ul className="rw-reasons">
          {r.wrong.map((x) => (
            <li key={x.index}>
              <b>{capitalise(x.text)}</b>
              <span>Label this part {x.labelName}.</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 2: revise and recall
// ---------------------------------------------------------------------------

function ReviseStep({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  if (!api.complete) {
    return (
      <>
        <QuestionStrip w={w} />
        <div className="rw-card">
          <span className="rr-eyebrow">Choose first</span>
          <p>Choose your {api.pick === 2 ? `two ${w.unit.plural}` : w.unit.singular} on the start screen, then return here to revise.</p>
          <div className="rw-row">
            <button type="button" className="rr-btn-primary" onClick={api.toStart}>
              Back to the start screen
            </button>
          </div>
        </div>
      </>
    );
  }
  const p = api.parts[api.part];
  if (!p) return null;
  const e = w.editions[p.caseStudy];
  const page = e?.revision[p.page];
  if (!e || !page) return null;
  if (p.kind === 'recall') return <RecallPart w={w} e={e} page={page} api={api} />;
  return (
    <>
      {api.part === 0 && (
        <div className="rw-note">
          {api.revSkipped ? (
            <>
              <span className="rr-eyebrow">Revision skipped</span>
              <p>You skipped the revision. Work through it here whenever you need it.</p>
            </>
          ) : !api.revDone ? (
            <>
              <span className="rr-eyebrow">Know this content already?</span>
              <p>Skip the revision and go straight to building your essay. You can return to it at any time.</p>
              <div className="rw-row">
                <button type="button" onClick={api.skipRevision}>
                  Skip the revision
                </button>
              </div>
            </>
          ) : null}
        </div>
      )}
      {page.recall && (
        <div className="rw-note">
          <span className="rr-eyebrow">Read carefully</span>
          <p>{WORKSHOP_FIXED_LINES.readCarefully}</p>
        </div>
      )}
      <span className="rr-eyebrow">{page.label ?? (api.pick === 2 ? w.caseStudies[p.caseStudy]?.head : undefined)}</span>
      <p className="rw-lede">{page.lede}</p>
      <div className="rw-grid">
        {page.cards.map((c, i) => (
          <div key={i} className="rw-card">
            <span className="rr-eyebrow">{c.tag}</span>
            <h4>{c.head}</h4>
            <ul>
              {c.points.map((pt, j) => (
                <li key={j}>{pt}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
}

function RecallPart({ w, e, page, api }: { w: WorkshopBlock; e: WorkshopEdition; page: WorkshopRevisionPage; api: WorkshopApi }) {
  const cur = api.currentRecall;
  const keys = useMemo(() => recallKeyFacts(e, page), [e, page]);
  if (!cur || !page.recall) return null;
  const st = cur.state;
  const lines = recallLines(st.text);
  const anchorScreen = e.anchorTheme !== undefined && (page.recall.facts ?? []).some((id) => e.facts[id]?.theme === e.anchorTheme);
  const example = copyFor(e, anchorScreen ? 'recallExampleAnchor' : 'recallExample', copyVars(w, api.chosen)).trim();
  const got = st.got.length;
  const miss = keys.length - got;
  return (
    <>
      <span className="rr-eyebrow">From memory</span>
      <h3 style={{ fontSize: '1.35rem', marginTop: -8 }}>{page.recall.name}</h3>
      <p className="rw-howto">
        Write down the six to eight most important facts from the screen you have just read: names, dates and what happened. Put one fact on each line. You cannot look back until you have checked your list. {WORKSHOP_FIXED_LINES.recallWhy}
      </p>
      <div className="rw-card">
        <textarea className="rw-textarea" aria-label="Your facts, one on each line" placeholder={example ? `One fact on each line. For example:\n${example}` : 'One fact on each line.'} value={st.text} readOnly={st.done} onChange={(ev) => api.setRecallText(ev.target.value)} />
        <div className="rw-wc">
          {lines.length === 1 ? '1 fact so far.' : `${lines.length} facts so far.`}
          {!st.done && lines.length < 6 ? ' Aim for six to eight.' : ''}
        </div>
        {!st.done && (
          <div className="rw-row">
            <button type="button" className="rr-btn-primary" disabled={lines.length === 0} onClick={api.checkRecall}>
              Check against the key facts
            </button>
          </div>
        )}
      </div>
      {st.done && (
        <>
          <div className="rw-rgrid">
            <div className="rw-card">
              <span className="rr-eyebrow">What you wrote</span>
              <ul>
                {lines.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </div>
            <div className="rw-card">
              <span className="rr-eyebrow">The key facts</span>
              <p className="rw-howto">{st.saved ? 'These are the facts you had.' : 'Select each one you wrote down, even if you used different words or left out a small detail.'}</p>
              <div className="rw-klist">
                {keys.map((k, j) => {
                  const on = st.got.includes(j);
                  return (
                    <button key={j} type="button" className={`rw-kfact${on ? ' rw-got' : ''}${st.saved && !on ? ' rw-miss' : ''}`} aria-pressed={on} disabled={st.saved} onClick={() => api.toggleRecallFact(j)}>
                      <span className="rw-kmark">{on ? '✓' : ''}</span>
                      <span>{k.text}.</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="rw-row">
            {!st.saved ? (
              <button type="button" className="rr-btn-primary" onClick={() => api.saveRecall(keys.length)}>
                Save my check
              </button>
            ) : (
              <button type="button" onClick={api.changeRecall}>
                Change my check
              </button>
            )}
          </div>
          {st.saved && (
            <div className={`rw-note ${miss ? 'rw-fix' : 'rw-ok'}`}>
              <span className="rr-eyebrow">
                You recalled {got} of {keys.length} key facts
              </span>
              <p>{miss ? `The ${miss === 1 ? 'one' : miss} you missed ${miss === 1 ? 'comes' : 'come'} back as flashcards before you plan your essay. Read ${miss === 1 ? 'it' : 'them'} again now.` : 'You had every key fact. Go on.'}</p>
            </div>
          )}
        </>
      )}
    </>
  );
}

/** The learner's thesis, kept in view on every screen after it is written. */
function ThesisStrip({ api }: { api: WorkshopApi }) {
  const t = api.arg.line.trim();
  if (!t) return null;
  return (
    <div className="rw-linestrip">
      <span className="rr-eyebrow">Your thesis</span>
      <span>{t}</span>
    </div>
  );
}

function EvidenceTable({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  const t = evidenceTable(w, api.chosen);
  return (
    <div className="rw-table-wrap">
      <table className="rw-table">
        <thead>
          <tr>
            {t.head.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {t.rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The table of themes, folded, on every step after the match is solved. */
function EvidenceFold({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  if (!api.matchSolved) return null;
  return (
    <details className="rw-card rw-fold">
      <summary>Open your table of {w.themeWord.plural}</summary>
      <EvidenceTable w={w} api={api} />
    </details>
  );
}

// ---------------------------------------------------------------------------
// Step 3: match each theme to the evidence that shows it
// ---------------------------------------------------------------------------

function MatchStep({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  if (!api.complete) return <ChooseFirst w={w} api={api} />;
  const e = w.editions[api.chosen[0]];
  const vars = copyVars(w, api.chosen);
  if (!api.matchCase) {
    return (
      <>
        <QuestionStrip w={w} />
        {api.matchSolved ? (
          <>
            <h3 style={{ fontSize: '1.35rem' }}>Your table of {w.themeWord.plural}</h3>
            <p className="rw-howto">{copyFor(e, 'matchTableHowto', vars)}</p>
            <div className="rw-card">
              <EvidenceTable w={w} api={api} />
            </div>
          </>
        ) : (
          <div className="rw-card">
            <span className="rr-eyebrow">Match first</span>
            <p>Your table of {w.themeWord.plural} appears here when you have matched all {numberWord(e.themes.length)}{api.twoCase ? ` for each ${w.unit.singular}` : ''}.</p>
          </div>
        )}
      </>
    );
  }
  const k = api.matchCase;
  const board = w.editions[k];
  const st = api.matchState;
  const lede = api.twoCase ? copyFor(e, api.part === 0 ? 'matchLede1' : 'matchLede2', vars) : copyFor(e, 'matchLede', vars);
  const leftOf = (ri: number) => Object.keys(st.map).map(Number).find((t) => st.map[t] === ri);
  return (
    <>
      <QuestionStrip w={w} />
      <p className="rw-lede">{lede}</p>
      <p className="rw-howto">{copyFor(e, 'matchHowto', vars)}</p>
      <div className="rw-pairs">
        <div className="rw-col" role="group" aria-label={capitalise(w.themeWord.plural)}>
          <div className="rw-colh">{capitalise(w.themeWord.singular)}</div>
          {board.themes.map((t, i) => {
            const paired = st.map[i] !== undefined;
            const mark = st.checked && paired ? (st.map[i] === i ? ' rw-right' : ' rw-wrong') : '';
            return (
              <button key={i} type="button" className={`rw-chip${api.matchSel === i ? ' rw-sel' : ''}${mark}`} aria-pressed={api.matchSel === i} disabled={st.solved} onClick={() => api.tapTheme(i)}>
                {paired && <span className="rw-badge">{i + 1}</span>}
                <span>{t.name}</span>
              </button>
            );
          })}
        </div>
        <div className="rw-col" role="group" aria-label="Evidence">
          <div className="rw-colh">{api.twoCase ? `Evidence from ${w.caseStudies[k]?.name ?? k}` : 'What it did'}</div>
          {api.matchDeal.map((ri) => {
            const li = leftOf(ri);
            const mark = st.checked && li !== undefined ? (li === ri ? ' rw-right' : ' rw-wrong') : '';
            return (
              <button key={ri} type="button" className={`rw-chip${mark}`} disabled={st.solved} onClick={() => api.tapEvidence(ri)}>
                {li !== undefined && <span className="rw-badge">{li + 1}</span>}
                <span>{board.themes[ri].matchLine}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="rw-row">
        {!st.solved && (
          <button type="button" className="rr-btn-primary" onClick={api.checkMatch}>
            Check my matches
          </button>
        )}
        {!st.solved && st.checks > 0 && (
          <button type="button" onClick={api.showMatches}>
            Show me the answers
          </button>
        )}
      </div>
      {(api.matchNote || st.solved) && (
        <div className={`rw-fb ${api.matchNote?.ok ?? st.solved ? 'rw-good' : 'rw-meh'}`} aria-live="polite">
          {api.matchNote?.text ?? `All ${numberWord(e.themes.length)} matched.`}
          {st.solved ? (api.twoCase && api.part === 0 ? ` Go on to do the same for ${w.caseStudies[api.chosen[1]]?.name}.` : ` Go on to see your table of ${w.themeWord.plural}.`) : ''}
        </div>
      )}
    </>
  );
}

function ChooseFirst({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  return (
    <>
      <QuestionStrip w={w} />
      <div className="rw-card">
        <span className="rr-eyebrow">Choose first</span>
        <p>Choose your {api.pick === 2 ? `two ${w.unit.plural}` : w.unit.singular} on the start screen, then return here.</p>
        <div className="rw-row">
          <button type="button" className="rr-btn-primary" onClick={api.toStart}>
            Back to the start screen
          </button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 4: decide your argument (readings, compare, rank, position)
// ---------------------------------------------------------------------------

function ArgueStep({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  if (!api.complete || !api.content) return <ChooseFirst w={w} api={api} />;
  const e = w.editions[api.chosen[0]];
  const vars = copyVars(w, api.chosen, api.paragraphs ?? undefined);
  const a = api.arg;
  const n = e.themes.length;
  const positions = api.content.positions;

  if (api.arguePart === 'readings') {
    return (
      <>
        <QuestionStrip w={w} />
        <p className="rw-lede">{copyFor(e, 'argueLede', vars)}</p>
        <div className={`rw-grid${w.readings.length === 3 ? ' rw-cols-3' : ''}`}>
          {w.readings.map((r, i) => (
            <div key={i} className="rw-card">
              <span className="rr-eyebrow">{r.label}</span>
              <h4>{r.title}</h4>
              <p>{r.text}</p>
            </div>
          ))}
        </div>
        <div className="rw-card">
          <span className="rr-eyebrow">{copyFor(e, 'balanceHead', vars)}</span>
          <p>{copyFor(e, 'balance', vars)}</p>
          <p>Historians disagree about which reading is right. In your paragraphs you can bring in a perspective and say how far you agree with it.</p>
        </div>
      </>
    );
  }

  if (api.arguePart === 'compare') {
    const [ca, cb] = api.chosen;
    const scale = w.scale ?? { labels: [], text: [] };
    const count = api.chosen.reduce((acc, k) => acc + Object.keys(a.cmp[k] ?? {}).length, 0);
    const sug = api.suggestion;
    return (
      <>
        <QuestionStrip w={w} />
        <h3 style={{ fontSize: '1.35rem' }}>Compare the two {w.unit.plural}</h3>
        <p className="rw-howto">{copyFor(e, 'compareHowto', vars)}</p>
        <div className="rw-cmp">
          <div className="rw-cmprow rw-cmphead">
            <span>{capitalise(w.themeWord.singular)}</span>
            <span>{w.caseStudies[ca]?.short}</span>
            <span>{w.caseStudies[cb]?.short}</span>
            <span />
          </div>
          {e.themes.map((t, ti) => {
            const va = a.cmp[ca]?.[ti];
            const vb = a.cmp[cb]?.[ti];
            const state = va === undefined || vb === undefined ? '' : va === vb ? 'Same effect' : 'Different effect';
            return (
              <div key={ti} className="rw-cmprow">
                <div>
                  <b>{t.name}</b>
                  <span className="rw-cq">{t.question}</span>
                </div>
                {[ca, cb].map((k) => (
                  <div key={k} className="rw-seg" role="group" aria-label={`${t.name}: ${w.caseStudies[k]?.name ?? k}`}>
                    {scale.labels.map((lbl, vi) => (
                      <button key={vi} type="button" title={capitalise(scale.text[vi] ?? lbl)} aria-pressed={a.cmp[k]?.[ti] === vi} onClick={() => api.judge(k, ti, vi)}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                ))}
                <span className={`rw-state${state ? (state === 'Same effect' ? ' rw-same' : ' rw-diff') : ''}`}>{state}</span>
              </div>
            );
          })}
        </div>
        <div className="rw-tally" aria-live="polite">
          {api.cmpDone
            ? `${compareSummary(w, e, api.chosen, a.cmp)}${sug !== null && positions[sug] ? ` That points towards this position: ${positions[sug].title.charAt(0).toLowerCase()}${positions[sug].title.slice(1)}. You decide after you have ranked the ${w.themeWord.plural}.` : ''}`
            : `Judge both ${w.unit.plural} on all ${numberWord(n)} ${w.themeWord.plural}. ${count} of ${n * 2} done.`}
        </div>
        <EvidenceFold w={w} api={api} />
      </>
    );
  }

  if (api.arguePart === 'rank') {
    const inList = api.paragraphList;
    const out = api.paragraphs ? leftOutThemes(n, inList) : [];
    return (
      <>
        <QuestionStrip w={w} />
        <h3 style={{ fontSize: '1.35rem' }}>Weigh the {w.themeWord.plural}</h3>
        <p className="rw-howto">{copyFor(e, 'rankHowto', vars)}</p>
        <ol className="rw-ranklist" aria-label="Your ranking">
          {a.rank.map((ti, pos) => (
            <li key={ti} className={api.paragraphs && !inList.includes(ti) ? 'rw-out' : ''}>
              <span className="rw-pos">{pos + 1}</span>
              <span>
                {e.themes[ti]?.term && <span className="rw-ctag">{e.themes[ti].term}</span>}
                {e.themes[ti]?.name}
              </span>
              <span className="rw-mv">
                <button type="button" aria-label={`Move ${e.themes[ti]?.name} up`} disabled={pos === 0} onClick={() => api.moveTheme(pos, -1)}>
                  ↑
                </button>
                <button type="button" aria-label={`Move ${e.themes[ti]?.name} down`} disabled={pos === a.rank.length - 1} onClick={() => api.moveTheme(pos, 1)}>
                  ↓
                </button>
              </span>
            </li>
          ))}
        </ol>
        <div className="rw-row">
          <button type="button" aria-pressed={a.ranked} onClick={api.keepOrder}>
            This order is mine
          </button>
          {w.coach && <CoachButton text="Challenge my ranking" />}
        </div>
        <div className="rw-tally" aria-live="polite">{a.ranked ? rankTally(w, e, api.chosen, api.paragraphs) : `Move the ${w.themeWord.plural} until the order is yours.`}</div>
        <div className="rw-wrow">
          <span>How many body paragraphs will you write?</span>
          <div className="rw-seg" role="group" aria-label="Body paragraphs">
            {[3, 4].map((v) => (
              <button key={v} type="button" aria-pressed={api.paragraphs === v} onClick={() => api.setParagraphs(v)}>
                {v === 3 ? 'Three' : 'Four'}
              </button>
            ))}
          </div>
        </div>
        <p className="rw-howto">{WORKSHOP_FIXED_LINES.paragraphsNote} You need to choose before you go on.</p>
        {out.length > 0 && <p className="rw-howto">Left out as a paragraph: {out.map((ti) => e.themes[ti]?.low).join(', ')}. {copyFor(e, 'leftOut', vars)}</p>}
        <div className="rw-card">
          <span className="rr-eyebrow">A test for your number one</span>
          <p>{copyFor(e, 'rankTest', vars)}</p>
        </div>
        <EvidenceFold w={w} api={api} />
      </>
    );
  }

  // position
  const sug = api.suggestion;
  const note = api.twoCase && api.cmpDone ? copyFor(e, 'comparePositionNote', { ...vars, overallBoth: overallBoth(w, e, api.chosen, a.cmp) }) : a.ranked ? copyFor(e, 'positionNote', vars) : 'Choose the position you are able to defend.';
  const picked = (field: 'reason' | 'counter', own: string, i: number) => a[field] === i && !own.trim();
  const fits = api.twoCase ? 'Closest to your comparison' : 'Closest to your ranking';
  return (
    <>
      <QuestionStrip w={w} />
      <h3 style={{ fontSize: '1.35rem' }}>Decide your argument</h3>
      <div className="rw-card">
        <span className="rr-eyebrow">{copyFor(e, 'positionLabel', vars)}</span>
        <p className="rw-howto">{note}{sug !== null ? ' That points towards the position marked below. You can still choose any position you are able to defend.' : ''}</p>
        <div className="rw-opts" role="group" aria-label="Your position">
          {positions.map((p, i) => (
            <button key={i} type="button" className="rw-opt" aria-pressed={a.pos === i} onClick={() => api.choosePosition(i)}>
              <b>
                {p.title}
                {i === sug && <span className="rw-fits">{fits}</span>}
              </b>
              <span>{p.detail}</span>
            </button>
          ))}
        </div>
      </div>
      {a.pos === null ? (
        <>
          <div className="rw-card">
            <span className="rr-eyebrow">2. Your main reason</span>
            <p className="rw-howto">Choose your position first. Your reasons appear here.</p>
          </div>
          <div className="rw-card">
            <span className="rr-eyebrow">3. The counter-argument you will answer</span>
            <p className="rw-howto">Choose your position first.</p>
          </div>
        </>
      ) : (
        <>
          <div className="rw-card">
            <span className="rr-eyebrow">2. Your main reason</span>
            <p className="rw-howto">Choose the reason you will prove. It gets a paragraph of its own, and the others support it.</p>
            <div className="rw-opts" role="group" aria-label="Your main reason">
              {sortedByRank(positions[a.pos].reasons, a.rank).map((r, pos) => (
                <button key={r.index} type="button" className="rw-opt" aria-pressed={picked('reason', a.ownR, r.index)} onClick={() => api.chooseReason(r.index)}>
                  <b>
                    {capitalise(r.text)}.{pos === 0 && a.ranked && <span className="rw-fits">Closest to your ranking</span>}
                  </b>
                </button>
              ))}
            </div>
            <div className="rw-own">
              <label htmlFor="rw-own-reason">Or write your own reason. Finish the sentence: because ...</label>
              <input id="rw-own-reason" className="rw-input" type="text" defaultValue={a.ownR} onBlur={(ev) => api.setOwnReason(ev.target.value)} />
            </div>
          </div>
          <div className="rw-card">
            <span className="rr-eyebrow">3. The counter-argument you will answer</span>
            <p className="rw-howto">Choose the strongest point against your position. It gets a paragraph, where you give it fairly and then answer it.</p>
            <div className="rw-opts" role="group" aria-label="The counter-argument you will answer">
              {sortedByRank(positions[a.pos].counters, a.rank).map((r) => (
                <button key={r.index} type="button" className="rw-opt" aria-pressed={picked('counter', a.ownC, r.index)} onClick={() => api.chooseCounter(r.index)}>
                  <b>{capitalise(r.text)}.</b>
                </button>
              ))}
            </div>
            <div className="rw-own">
              <label htmlFor="rw-own-counter">Or write your own. Finish the sentence: although ...</label>
              <input id="rw-own-counter" className="rw-input" type="text" defaultValue={a.ownC} onBlur={(ev) => api.setOwnCounter(ev.target.value)} />
            </div>
          </div>
        </>
      )}
      <div className="rw-card">
        <span className="rr-eyebrow">Your thesis</span>
        <p className="rw-howto">This sentence is built from your choices. Rewrite it until it sounds like you. Changing a choice above builds it again.</p>
        <textarea className="rw-textarea" style={{ minHeight: 110 }} aria-label="Your thesis" placeholder="Make your choices above and your thesis appears here." value={a.line} onChange={(ev) => api.setThesis(ev.target.value)} onBlur={api.commitThesis} />
        {w.coach && (
          <div className="rw-row">
            <CoachButton text="Check my thesis with the coach" />
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 5: recall what you missed (the flashcard deck)
// ---------------------------------------------------------------------------

function MissedStep({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  if (!api.complete) return <ChooseFirst w={w} api={api} />;
  const total = api.deck.length;
  const pile = api.pile;
  const front = pile.queue[0] ? cardOf(w, pile.queue[0]) : null;
  if (total === 0) {
    return (
      <>
        <QuestionStrip w={w} />
        <ThesisStrip api={api} />
        <div className="rw-card">
          <span className="rr-eyebrow">Nothing to recall</span>
          <p>You recalled every key fact during the revision. Go on to plan your paragraphs.</p>
        </div>
        <EvidenceFold w={w} api={api} />
      </>
    );
  }
  return (
    <>
      <QuestionStrip w={w} />
      <ThesisStrip api={api} />
      <div className="rw-note">
        <span className="rr-eyebrow">Why this matters</span>
        <p>{WORKSHOP_FIXED_LINES.flashcardsWhy1}</p>
        <p>{WORKSHOP_FIXED_LINES.flashcardsWhy2}</p>
      </div>
      <div className="rw-fcbar" aria-live="polite">
        <span>
          {pile.got.length} of {total} cards: got it
        </span>
        <span className="rw-fctrack">
          <span className="rw-fcfill" style={{ width: `${Math.round((100 * pile.got.length) / total)}%` }} />
        </span>
      </div>
      {!front ? (
        <div className="rw-note rw-ok">
          <span className="rr-eyebrow">Every card: got it</span>
          <p>You can now recall every fact you missed. Go on to plan your paragraphs, where you will choose from these facts again.</p>
        </div>
      ) : (
        <div className={`rw-fcard${pile.flip ? ' rw-back' : ''}`}>
          <span className="rr-eyebrow">{front.group}</span>
          <p className="rw-fcq">{front.cue}</p>
          {!pile.flip ? (
            <>
              <p className="rw-howto">Say or write the answer before you turn the card.</p>
              <div className="rw-row">
                <button type="button" className="rr-btn-primary" onClick={api.turnCard}>
                  Turn the card
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="rw-fca">
                <span className="rr-eyebrow">Answer</span>
                <p>{front.answer}.</p>
              </div>
              <p className="rw-howto">Did you have it? Be honest: a card you mark Not yet comes back at the end of the pile.</p>
              <div className="rw-row">
                <button type="button" className="rr-btn-primary" onClick={() => api.answerCard(true)}>
                  Got it
                </button>
                <button type="button" onClick={() => api.answerCard(false)}>
                  Not yet
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <EvidenceFold w={w} api={api} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Steps 6 to 10: the paragraphs overview, the introduction, one step per
// paragraph (topic sentence, facts, perspective, write), the conclusion.
// ---------------------------------------------------------------------------

function DecideFirst({ api }: { api: WorkshopApi }) {
  return (
    <div className="rw-card">
      <span className="rr-eyebrow">Decide first</span>
      <p>Your paragraphs come from your ranking and your argument. Decide your argument, then return here.</p>
      <div className="rw-row">
        <button type="button" className="rr-btn-primary" onClick={() => api.goToStep('argue')}>
          Go to Decide your argument
        </button>
      </div>
    </div>
  );
}

function ParagraphsStep({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  if (!api.complete) return <ChooseFirst w={w} api={api} />;
  const e = w.editions[api.chosen[0]];
  if (!api.argDone || !api.paragraphs) {
    return (
      <>
        <QuestionStrip w={w} />
        <DecideFirst api={api} />
      </>
    );
  }
  const vars = copyVars(w, api.chosen, api.paragraphs);
  const routine = [
    ['Choose your topic sentence', 'This is the point the paragraph will make. Choose the one that fits your thesis, or write your own.'],
    ['Select your facts', `These are the facts that prove your point. You know them already. The task is choosing the ones that belong here${api.twoCase ? `, from both ${w.unit.plural}` : ''}.`],
    ['Add a perspective (optional)', "Bring in a historian's view where it helps your argument, and say how far you agree. You can leave it out of a paragraph."],
    ['Write the paragraph', 'Write it from your plan in your own words, check it, then write it again by hand.'],
  ];
  const out = leftOutThemes(e.themes.length, api.paragraphList);
  return (
    <>
      <QuestionStrip w={w} />
      <ThesisStrip api={api} />
      <p className="rw-lede">{copyFor(e, 'paragraphsLede', vars)}</p>
      <div className="rw-grid rw-cols-4">
        {routine.map(([head, text], i) => (
          <div key={i} className="rw-card">
            <div className="rw-num">{i + 1}</div>
            <h4>{head}</h4>
            <p>{text}</p>
          </div>
        ))}
      </div>
      <span className="rr-eyebrow">
        Your {numberWord(api.paragraphs)} paragraphs, in the order you ranked them
      </span>
      <div className="rw-planlist">
        {api.paragraphList.map((theme, i) => {
          const n = i + 1;
          const stepIdx = api.steps.findIndex((s) => s.key === `p${n}`);
          const done = stepIdx >= 0 && api.isDone(api.steps[stepIdx]);
          const locked = stepIdx >= 0 && api.isLocked(stepIdx);
          return (
            <div key={theme} className="rw-planrow">
              <div>
                <span className="rr-eyebrow">Paragraph {n}</span>
                <span className={`rw-state ${done ? 'rw-same' : ''}`}>{done ? 'Written' : 'To write'}</span>
                {api.reasonTheme === theme && <span className="rw-state rw-diff">Your main reason</span>}
                {api.counterTheme === theme && <span className="rw-state rw-diff">Your counter-argument</span>}
                <h4>{e.themes[theme]?.name}</h4>
                <p className="rw-howto">{e.themes[theme]?.what}</p>
              </div>
              <button type="button" className={done ? '' : 'rr-btn-primary'} disabled={locked} title={locked ? 'Write your introduction and the earlier paragraphs first' : undefined} onClick={() => api.goToStep(`p${n}`)}>
                {done ? `Open paragraph ${n}` : `Go to paragraph ${n}`}
              </button>
            </div>
          );
        })}
      </div>
      {out.length > 0 && <p className="rw-howto">Left out: {out.map((t) => e.themes[t]?.low).join(', ')}. {copyFor(e, 'leftOut', vars)}</p>}
      <EvidenceFold w={w} api={api} />
    </>
  );
}

/** A note beside the box: never inserted into the text (Leah, rule 18). */
function Note({ label, text }: { label?: string; text: string }) {
  return (
    <div className="rw-chipnote">
      {label && <span className="rw-sl">{label}</span>}
      <span>{text}</span>
    </div>
  );
}

function Starters({ list }: { list: Array<{ label: string; text: string }> }) {
  if (!list.length) return null;
  return (
    <>
      <h4>Sentence starters</h4>
      <div className="rw-bank">
        {list.map((s, i) => (
          <Note key={i} label={s.label} text={`${s.text}...`} />
        ))}
      </div>
    </>
  );
}

/** Save a text file through a download link; the message says what happened. */
function downloadTextFile(filename: string, text: string): string {
  try {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return 'Saved to your downloads.';
  } catch {
    return 'This device could not save the file. Copy your writing instead.';
  }
}

/** The write box with its notes, the word count, the quick check and the handwriting card. */
function WritePart({ w, api, part, title, partName, target, aside }: { w: WorkshopBlock; api: WorkshopApi; part: PartId; title: string; partName: 'introduction' | 'paragraph' | 'conclusion'; target: readonly [number, number]; aside: React.ReactNode }) {
  const d = api.draftOf(part);
  const items = api.checkItemsOf(part);
  const words = wordCount(d.text);
  const [saved, setSaved] = useState<string | null>(null);
  // A check on an empty box: the hook records nothing, so the note is local.
  const [triedEmpty, setTriedEmpty] = useState(false);
  const empty = !d.text.trim();
  const check = () => {
    setTriedEmpty(empty);
    if (!empty) api.checkPart(part);
  };
  const mins = partName === 'paragraph' ? 8 : 5;
  const saveFile = () => {
    const text = d.text.trim();
    if (!text) {
      setSaved('There is nothing to save yet.');
      return;
    }
    setSaved(downloadTextFile(`${title.toLowerCase().replace(/\s+/g, '-')}.txt`, `${w.question}\n\n${title}\n\n${text}\n`));
  };
  return (
    <>
      <div className="rw-write">
        <aside className="rw-waside">{aside}</aside>
        <div className="rw-wmain">
          <textarea className="rw-textarea" style={{ minHeight: 330 }} aria-label={title} placeholder={`Write your ${partName} here, in your own words.`} value={d.text} onChange={(ev) => api.setDraftText(part, ev.target.value)} onBlur={() => api.flushDraft(part)} />
          <div className="rw-wc">
            {words} words. Aim for {target[0]} to {target[1]}.
          </div>
          <div className="rw-row">
            <button type="button" className="rr-btn-primary" onClick={check}>
              Check my {partName}
            </button>
          </div>
        </div>
      </div>
      {(d.checked || (triedEmpty && empty)) && (
        <div className="rw-feedback" aria-live="polite">
          <span className="rr-eyebrow">Feedback on your {partName}</span>
          {empty ? (
            <>
              <h3>Nothing to check yet</h3>
              <p>Write your {partName} first, then check it.</p>
            </>
          ) : !items ? (
            <>
              <h3>You checked this {partName} last time</h3>
              <p>Check it again to see the feedback.</p>
            </>
          ) : (
            <>
              <h3>{checkHeadline(items)}</h3>
              <div className="rw-fgrid">
                {items.map((it, i) => (
                  <div key={i} className="rw-fitem">
                    <span className="rw-ft">{it.label}</span>
                    <span className={`rw-ftag ${it.ok ? 'rw-ok' : 'rw-fix'}`}>{it.ok ? 'In place' : 'To fix'}</span>
                    <span className="rw-fm">{it.message}</span>
                  </div>
                ))}
              </div>
              {w.coach && <p className="rw-howto">The coach's read of your {partName} is not connected in this build. For now you have the quick check above, which compares your writing with your plan.</p>}
              <p>{items.some((i) => !i.ok) ? 'Fix what you can, then check again.' : 'You can move on, or improve it and check again.'}</p>
            </>
          )}
        </div>
      )}
      {d.checked && !empty && (
        <div className="rw-card rw-handcard">
          <span className="rr-eyebrow">On paper</span>
          <h3>Now write it by hand</h3>
          <p>
            You will write this essay by hand in the exam. Writing it once now, from memory, shows you what you really know. Look away from the screen. On paper, write your {partName} again from memory, using only your plan. Give yourself about {mins} minutes, which is your exam pace. Then come back, compare it with your typed version, and underline anything you left out or got wrong.
          </p>
          <label className="rw-tick">
            <input type="checkbox" checked={d.hand} onChange={(ev) => api.toggleHand(part, ev.target.checked)} /> <span>I have written it by hand</span>
          </label>
          <div className="rw-row">
            <button type="button" onClick={saveFile}>
              Save this {partName} as a file
            </button>
            {saved && <span className="rw-gatehint">{saved}</span>}
          </div>
        </div>
      )}
      <EvidenceFold w={w} api={api} />
    </>
  );
}

function IntroStep({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  if (!api.complete) return <ChooseFirst w={w} api={api} />;
  const e = w.editions[api.chosen[0]];
  if (!api.argDone || !api.content) {
    return (
      <>
        <QuestionStrip w={w} />
        <DecideFirst api={api} />
      </>
    );
  }
  return (
    <>
      <QuestionStrip w={w} />
      <ThesisStrip api={api} />
      <WritePart
        w={w}
        api={api}
        part="intro"
        title="Introduction"
        partName="introduction"
        target={WORKSHOP_WORD_TARGETS.intro}
        aside={
          <>
            <h4>Put your introduction together</h4>
            <p className="rw-howto">Use these as notes. Write the introduction in your own words, in this order.</p>
            <div className="rw-bank">
              <Note label="First, the context" text={api.content.context} />
              <Note label="Second, your thesis" text={api.arg.line.trim() || 'You have not decided your argument yet. Go back to Decide your argument.'} />
              <Note label="Third, your outline" text={outlineText(w, e, api.chosen, api.paragraphList)} />
            </div>
            <h4>An introduction must</h4>
            <ul>
              {w.writing.introMust.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </>
        }
      />
    </>
  );
}

function ConclusionStep({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  if (!api.complete) return <ChooseFirst w={w} api={api} />;
  if (!api.argDone) {
    return (
      <>
        <QuestionStrip w={w} />
        <DecideFirst api={api} />
      </>
    );
  }
  const e = w.editions[api.chosen[0]];
  const starters = w.writing.conclusionStarters ?? [{ label: 'Answer', text: copyFor(e, 'conclusionAnswerStarter', copyVars(w, api.chosen)) }, { label: 'The other view', text: 'It is true that ' }, { label: 'Judgement', text: 'On balance, ' }];
  return (
    <>
      <QuestionStrip w={w} />
      <ThesisStrip api={api} />
      <WritePart
        w={w}
        api={api}
        part="conclusion"
        title="Conclusion"
        partName="conclusion"
        target={WORKSHOP_WORD_TARGETS.conclusion}
        aside={
          <>
            <h4>A conclusion must</h4>
            <ul>
              {w.writing.conclusionMust.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
            <Starters list={starters} />
          </>
        }
      />
    </>
  );
}

function ParagraphStep({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  if (!api.complete) return <ChooseFirst w={w} api={api} />;
  const n = api.step?.n ?? 1;
  const theme = api.themeOfParagraph(n);
  if (!api.argDone || theme === undefined || !api.content || !api.paragraphs) {
    return (
      <>
        <QuestionStrip w={w} />
        <DecideFirst api={api} />
      </>
    );
  }
  const e = w.editions[api.chosen[0]];
  const t = e.themes[theme];
  const plan = api.planOf(theme);
  const ts = api.topicSentenceOf(theme);
  const head = (
    <>
      <QuestionStrip w={w} />
      <ThesisStrip api={api} />
      <span className="rr-eyebrow">
        Paragraph {n} of {numberWord(api.paragraphs)}
        {t.term ? ` · ${t.term} ${w.themeWord.singular}` : ''}
      </span>
      <h3 style={{ fontSize: '1.35rem', marginTop: -8 }}>{t.name}</h3>
    </>
  );
  const roles: string[] = [];
  if (e.anchorTheme === theme) roles.push(`This is your short-term ${w.themeWord.singular}. Every essay on this question needs it.`);
  if (api.reasonTheme === theme) roles.push(`This paragraph carries your main reason: ${api.reasonText}.`);
  if (api.counterTheme === theme) roles.push(`This paragraph carries your counter-argument: ${api.counterText}. Give it fairly, with evidence, then answer it.`);

  if (api.part === 0) {
    const options = api.content.topicSentences[theme] ?? [];
    const fit = api.arg.pos ?? -1;
    const ja = api.twoCase ? api.arg.cmp[api.chosen[0]]?.[theme] : undefined;
    const jb = api.twoCase ? api.arg.cmp[api.chosen[1]]?.[theme] : undefined;
    const judged = ja !== undefined && jb !== undefined && w.scale ? `When you compared the ${w.unit.plural}, you judged that on this ${w.themeWord.singular} ${ja === jb ? copyFor(e, 'bothSame', { ...copyVars(w, api.chosen), j: w.scale.text[ja] }) : copyFor(e, 'bothDifferent', { ...copyVars(w, api.chosen), ja: w.scale.text[ja], jb: w.scale.text[jb] })}.` : '';
    return (
      <>
        {head}
        {(judged || roles.length > 0) && (
          <div className="rw-note">
            {judged && <p>{judged}</p>}
            {roles.map((r, i) => (
              <p key={i}>{r}</p>
            ))}
          </div>
        )}
        <div className="rw-card">
          <span className="rr-eyebrow">1. Choose your topic sentence</span>
          <p className="rw-howto">These are all real topic sentences, and each one argues something different. Choose the one you want to defend, or write your own.</p>
          <div className="rw-opts" role="group" aria-label="Topic sentences">
            {options.map((o, i) => (
              <button key={i} type="button" className="rw-opt" aria-pressed={plan.ts === i} onClick={() => api.chooseTopicSentence(theme, i)}>
                <b>
                  {String.fromCharCode(65 + i)}. {o.text}
                  {i === fit && <span className="rw-fits">Closest to your thesis</span>}
                </b>
              </button>
            ))}
          </div>
          <div className="rw-own">
            <label htmlFor="rw-own-ts">Or write your own topic sentence</label>
            <input id="rw-own-ts" className="rw-input" type="text" placeholder="Make a point that answers the question. Do not start with a date." defaultValue={plan.own} onChange={(ev) => api.setOwnTopicSentence(theme, ev.target.value)} onBlur={() => api.commitOwnTopicSentence(theme)} />
            <div className="rw-row">
              <button type="button" onClick={() => api.useOwnTopicSentence(theme)}>
                Use my own sentence
              </button>
              {w.coach && <CoachButton text="Check it with the coach" />}
            </div>
          </div>
          {ts && (
            <div className="rw-note rw-ok">
              <span className="rr-eyebrow">Your topic sentence</span>
              <p>{ts}</p>
              <p>{plan.ts === 'own' ? 'Check that it makes a point that answers the question. If it only reports an event, your paragraph will tell the story.' : options[plan.ts as number]?.need}</p>
            </div>
          )}
        </div>
      </>
    );
  }

  if (api.part === 1) {
    const offered = api.factsOffered(theme);
    const note = api.selectionNote;
    return (
      <>
        {head}
        {ts && (
          <div className="rw-note rw-ok">
            <span className="rr-eyebrow">Your topic sentence</span>
            <p>{ts}</p>
          </div>
        )}
        <div className="rw-card">
          <span className="rr-eyebrow">2. Select your facts</span>
          <p className="rw-howto">
            You should know all of these facts already, and every one of them belongs somewhere in this essay. Select at least {w.minFacts} that prove your topic sentence{api.twoCase ? `, with at least one from each ${w.unit.singular}` : ''}. Leave the rest for the paragraphs where they belong.
          </p>
          <div className="rw-infos" role="group" aria-label="Facts">
            {offered.map((f) => {
              const on = plan.sel.includes(f.key);
              const mark = plan.checked || plan.locked ? (on && f.relevant ? ' rw-right' : on && !f.relevant ? ' rw-wrong' : !on && f.relevant && plan.locked ? ' rw-also' : '') : on ? ' rw-sel' : '';
              return (
                <span key={f.key} className="rw-fwrap">
                  <button type="button" className={`rw-chip${mark}`} aria-pressed={on} disabled={plan.locked} onClick={() => api.toggleFact(theme, f.key)}>
                    {api.twoCase && <span className="rw-ctag">{w.caseStudies[f.caseStudy]?.short}</span>}
                    <span>{f.text}</span>
                  </button>
                  <FactNote fact={f} />
                </span>
              );
            })}
          </div>
          <div className="rw-row">
            {!plan.locked ? (
              <button type="button" className="rr-btn-primary" onClick={() => api.checkFacts(theme)}>
                Check my selection
              </button>
            ) : (
              <button type="button" onClick={() => api.changeFacts(theme)}>
                Change my selection
              </button>
            )}
          </div>
          {(note || plan.locked) && <div className={`rw-fb ${plan.locked ? 'rw-good' : 'rw-meh'}`}>{plan.locked ? 'Your evidence is in your plan. The facts with a dotted outline also fit this paragraph, if you want to add them.' : note}</div>}
          {(plan.checked || plan.locked) && (
            <ul className="rw-reasons">
              {offered
                .filter((f) => !f.relevant && plan.sel.includes(f.key))
                .map((f) => (
                  <li key={f.key}>
                    <b>{f.text}</b>
                    <span>{f.why}</span>
                  </li>
                ))}
            </ul>
          )}
        </div>
        <EvidenceFold w={w} api={api} />
      </>
    );
  }

  if (api.part === 2) {
    const list = api.perspectivesFor(theme);
    const pp = api.perspectiveOf(theme);
    const done = api.perspDone(theme);
    return (
      <>
        {head}
        {ts && (
          <div className="rw-note rw-ok">
            <span className="rr-eyebrow">Your topic sentence</span>
            <p>{ts}</p>
          </div>
        )}
        <div className="rw-card">
          <span className="rr-eyebrow">3. Add a perspective (optional)</span>
          <p className="rw-howto">Each of these reads this {w.themeWord.singular} differently. {WORKSHOP_FIXED_LINES.perspectiveOptional}</p>
          <div className="rw-opts" role="group" aria-label="Perspectives">
            {list.map((p, i) => (
              <button key={i} type="button" className="rw-opt" aria-pressed={plan.persp === i && !plan.noP} onClick={() => api.choosePerspective(theme, i)}>
                <b>{p.name}</b>
                <span>{p.line}</span>
              </button>
            ))}
          </div>
          <div className="rw-row">
            <button type="button" aria-pressed={plan.noP} onClick={() => api.leavePerspective(theme)}>
              Leave a perspective out of this paragraph
            </button>
          </div>
          {plan.noP && (
            <div className="rw-note rw-ok">
              <span className="rr-eyebrow">In your paragraph</span>
              <p>You will not use a perspective in this paragraph. You can still bring one into another paragraph.</p>
            </div>
          )}
        </div>
        {pp && (
          <div className="rw-card">
            <span className="rr-eyebrow">4. Decide how you will use it</span>
            <div className="rw-wrow">
              <span>What does this view do to your topic sentence?</span>
              <div className="rw-seg" role="group" aria-label="What this view does">
                {PERSPECTIVE_USE.map(([, label], v) => (
                  <button key={v} type="button" aria-pressed={plan.puse === v} onClick={() => api.setPerspectiveUse(theme, v)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="rw-wrow">
              <span>How far do you agree with it?</span>
              <div className="rw-seg" role="group" aria-label="How far you agree">
                {PERSPECTIVE_AGREE.map((label, v) => (
                  <button key={v} type="button" aria-pressed={plan.pagree === v} onClick={() => api.setPerspectiveAgree(theme, v)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className={`rw-note${done ? ' rw-ok' : ''}`}>
              <span className="rr-eyebrow">In your paragraph</span>
              <p>{done && plan.pagree !== null ? `State this view in your own words. Then say that ${PERSPECTIVE_AGREE_TEXT[plan.pagree]}, and use your evidence to show why.` : 'Make both choices above to finish this plan.'}</p>
            </div>
          </div>
        )}
      </>
    );
  }

  // part 3: write
  const pp = api.perspectiveOf(theme);
  const planned = api.factsOffered(theme).filter((f) => plan.locked && plan.sel.includes(f.key) && f.relevant);
  const starters = (w.writing.paragraphStarters ?? [{ label: 'Analysis', text: 'This matters because ' }, { label: 'Judging the view', text: 'The evidence supports this view only in part, because ' }, { label: 'Link', text: 'This shows that ' }]).filter((x) => !!pp || x.label !== 'Judging the view');
  return (
    <>
      {head}
      <WritePart
        w={w}
        api={api}
        part={partIdFor(theme)}
        title={partTitle(partIdFor(theme), api.paragraphList)}
        partName="paragraph"
        target={WORKSHOP_WORD_TARGETS.paragraph[api.paragraphs]}
        aside={
          <>
            <h4>Your topic sentence</h4>
            <div className="rw-bank">
              <Note text={ts} />
            </div>
            <h4>Your evidence</h4>
            <div className="rw-bank">
              {planned.map((f) => (
                <span key={f.key} className="rw-fwrap">
                  <Note label={api.twoCase ? w.caseStudies[f.caseStudy]?.short : undefined} text={f.text} />
                  <FactNote fact={f} />
                </span>
              ))}
            </div>
            {pp && (
              <>
                <h4>Your perspective</h4>
                <div className="rw-bank">
                  <Note label={pp.name} text={`${pp.opener}...`} />
                </div>
                <p className="rw-howto">
                  {pp.line} You said that {plan.pagree !== null ? PERSPECTIVE_AGREE_TEXT[plan.pagree] : 'you will weigh it'}.
                </p>
              </>
            )}
            <p className="rw-howto">Use your plan as notes. Write every sentence in your own words.</p>
            <h4>{numberWord(w.jobs.length) === 'five' ? 'Five jobs, in this order' : 'The jobs, in this order'}</h4>
            <ol>
              {w.jobs.map((j, i) => (
                <li key={i}>
                  <b>{j.name}. </b>
                  {j.text}
                </li>
              ))}
            </ol>
            <Starters list={starters} />
          </>
        }
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Step 11: the essay, assembled; copy it, save it, or start again.
// ---------------------------------------------------------------------------

function EssayStep({ w, api }: { w: WorkshopBlock; api: WorkshopApi }) {
  const [msg, setMsg] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const pre = useRef<HTMLDivElement>(null);
  if (!api.complete) return <ChooseFirst w={w} api={api} />;
  const text = api.essayText;
  const total = api.essayOrder.length;
  const full = () => `${w.question}\n\n${text}\n`;
  const copy = () => {
    if (!text) {
      setMsg('There is nothing to copy yet.');
      return;
    }
    const fallback = () => {
      try {
        const range = document.createRange();
        range.selectNodeContents(pre.current!);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        setMsg('Selected. Copy it with your keyboard or menu.');
      } catch {
        setMsg('Select the essay and copy it with your keyboard or menu.');
      }
    };
    try {
      navigator.clipboard.writeText(full()).then(() => setMsg('Copied, with the question at the top.'), fallback);
    } catch {
      fallback();
    }
  };
  const saveFile = () => {
    if (!text) {
      setMsg('There is nothing to save yet.');
      return;
    }
    setMsg(downloadTextFile(`${w.brand.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'essay'}-essay.txt`, full()));
  };
  const savePdf = () => {
    if (!text) {
      setMsg('There is nothing to save yet.');
      return;
    }
    const win = window.open('', '_blank');
    if (!win) {
      setMsg('Your browser blocked the print window. Allow pop-ups for this site, or copy your essay instead.');
      return;
    }
    win.document.write(essayPrintHtml(w.question, text));
    win.document.close();
    win.focus();
    win.print();
    setMsg('In the print dialog, choose Save as PDF.');
  };
  return (
    <>
      <QuestionStrip w={w} />
      <p className="rw-lede">{!api.argDone ? 'Your essay will appear here as you write it, part by part. Decide your argument first.' : api.essayWritten === total ? 'Here is your essay, in your own words.' : `Here is your essay so far. ${api.essayWritten} of ${total} parts are written.`}</p>
      <div ref={pre} className={`rw-essay${text ? '' : ' rw-empty'}`}>
        {text || 'Nothing written yet. Start with your introduction.'}
      </div>
      <div className="rw-wc">
        {wordCount(text)} words. A full essay is usually {thousands(ESSAY_WORDS_USUAL[0])} to {thousands(ESSAY_WORDS_USUAL[1])}.
      </div>
      <div className="rw-wc">{api.handwritten.length ? `Written by hand: ${joinList(api.handwritten)}.` : 'You have not marked any part as written by hand yet.'}</div>
      <div className="rw-feedback">
        <span className="rr-eyebrow">Next · Send it</span>
        <h3>Send your essay to your teacher</h3>
        <p>Read your essay through once from start to finish. Then copy it or save it as a file, and send it to your teacher.</p>
        <div className="rw-row">
          <button type="button" className="rr-btn-primary" onClick={copy}>
            Copy my essay
          </button>
          <button type="button" onClick={saveFile}>
            Save as a text file
          </button>
          <button type="button" onClick={savePdf}>
            Save as PDF
          </button>
        </div>
        <p aria-live="polite" style={{ margin: 0, minHeight: '1.4em' }}>
          {msg ?? ''}
        </p>
      </div>
      <div className="rw-row" style={{ marginTop: 24 }}>
        <button type="button" onClick={() => setConfirm(true)}>
          Clear everything and start again
        </button>
        {confirm && (
          <button
            type="button"
            className="rr-btn-primary"
            onClick={() => {
              setConfirm(false);
              api.resetAll();
            }}
          >
            Yes, clear everything
          </button>
        )}
      </div>
      <EvidenceFold w={w} api={api} />
    </>
  );
}

/** The "?" on a fact: a neutral note, with no hint about which paragraph it belongs in. */
function FactNote({ fact }: { fact: { text: string; note?: string } }) {
  const [open, setOpen] = useState(false);
  if (!fact.note) return null;
  return (
    <>
      <button type="button" className="rw-qmark" aria-label="Find out about this fact" title="Find out about this fact" onClick={() => setOpen(true)}>
        ?
      </button>
      {open && (
        <div className="rr-modal" role="dialog" aria-label="About this fact" onClick={() => setOpen(false)}>
          <div className="rr-modal-card" onClick={(ev) => ev.stopPropagation()}>
            <span className="rr-eyebrow">About this fact</span>
            <p>
              <b>{fact.text}</b>
            </p>
            <p>{fact.note}</p>
            <div>
              <button type="button" className="rr-btn-primary" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** An AI button in the with-coach version. The coach's own replies are a later build; the button says so when pressed. */
function CoachButton({ text }: { text: string }) {
  const [said, setSaid] = useState(false);
  return (
    <span className="rw-row">
      <button type="button" onClick={() => setSaid(true)}>
        {text} <span className="rr-chip rr-chip-accent">AI</span>
      </button>
      {said && <span className="rw-gatehint">The coach is not connected in this build.</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Coach: the quick answers for this step. The coach's own replies are a later,
// metered build (plan §7); the button says so rather than pretending.
// ---------------------------------------------------------------------------

function CoachQuick({ w, api, onClose }: { w: WorkshopBlock; api: WorkshopApi; onClose: () => void }) {
  const e = (api.chosen[0] && w.editions[api.chosen[0]]) || Object.values(w.editions)[0];
  const group = api.phase === 'steps' ? (api.step?.id === 'paragraph' ? 'paragraphs' : api.step?.id === 'intro' || api.step?.id === 'conclusion' ? 'write' : api.step?.id) : 'choose';
  const quick = [...((group && e?.quick[group]) ?? []), ...(api.step?.id === 'paragraph' ? e?.quick.write ?? [] : [])];
  return (
    <div className="rr-modal" role="dialog" aria-label="Coach" onClick={onClose}>
      <div className="rr-modal-card" onClick={(ev) => ev.stopPropagation()}>
        <span className="rr-eyebrow">Your coach · {api.phase === 'steps' ? api.step?.title : 'The start'}</span>
        {quick.length ? (
          <div className="rw-quick">
            {quick.map(([q, a], i) => (
              <details key={i}>
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        ) : (
          <p className="rw-howto">No quick answers on this step yet.</p>
        )}
        <p className="rw-howto">Your own questions to the coach are not connected in this build.</p>
        <div>
          <button type="button" className="rr-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
